import { useMemo, useState } from 'react';
import { gossipCategories, latestItems, type GossipCategory } from '@/mocks/gossip';
import SectionHeading from '@/pages/home/components/SectionHeading';

type Filter = '全部' | GossipCategory;

const filters: Filter[] = ['全部', ...gossipCategories];

const categoryTone: Record<string, string> = {
  明星黑料: 'bg-primary-100 text-primary-700',
  网红黑料: 'bg-accent-100 text-accent-800',
  综艺风波: 'bg-secondary-100 text-secondary-800',
  直播带货: 'bg-primary-50 text-primary-700',
  情感纠葛: 'bg-accent-50 text-accent-800',
  塌房现场: 'bg-secondary-200 text-secondary-900',
};

export default function LatestFeed() {
  const [active, setActive] = useState<Filter>('全部');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const list = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return latestItems.filter((item) => {
      const matchCategory = active === '全部' || item.category === active;
      const matchKeyword =
        !keyword ||
        item.title.toLowerCase().includes(keyword) ||
        item.excerpt.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });
  }, [active, query]);

  return (
    <section id="latest" className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="LATEST · 最新黑料合集"
          title="最新黑料合集 · 今日爆料一网打尽"
          desc="按分类或关键词快速筛选，点击卡片即可展开事件梗概。列表仅作用于本页已有内容，不涉及任何跳转与提交。"
        />

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="黑料分类筛选">
            {filters.map((filter) => {
              const isActive = filter === active;
              return (
                <button
                  key={filter}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(filter)}
                  className={`px-4 py-2 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900'
                  }`}
                >
                  {filter}
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-72 shrink-0">
            <i
              className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
              aria-hidden="true"
            ></i>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索标题、标签或关键词"
              aria-label="搜索爆料内容"
              className="w-full pl-9 pr-9 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 font-body"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="清空搜索"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-foreground-400 hover:bg-background-200 hover:text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-sm" aria-hidden="true"></i>
              </button>
            )}
          </div>
        </div>

        {list.length === 0 ? (
          <div className="py-16 text-center rounded-lg bg-background-100 border border-background-200">
            <i className="ri-search-eye-line text-4xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-3 text-sm text-foreground-600 font-body">没有找到匹配的爆料，换个关键词或分类试试。</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {list.map((item) => {
              const expanded = expandedId === item.id;
              return (
                <article
                  key={item.id}
                  className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
                >
                  <div className="relative w-full h-44 md:h-48 overflow-hidden bg-background-200">
                    <img
                      src={item.image}
                      alt={item.title}
                      title={`${item.title} - 黑料吃瓜网`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span
                      className={`absolute top-3 left-3 px-2.5 py-1 rounded-md text-[11px] md:text-xs font-label ${
                        categoryTone[item.category] || 'bg-secondary-100 text-secondary-800'
                      }`}
                    >
                      {item.category}
                    </span>
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/60 text-background-50 text-[11px] font-label">
                      <i className="ri-fire-line text-accent-400" aria-hidden="true"></i>
                      {item.heat}
                    </span>
                  </div>

                  <div className="flex flex-col flex-1 p-4 md:p-5">
                    <h3 className="font-heading text-lg md:text-xl text-foreground-950 leading-snug mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed font-body line-clamp-3">{item.excerpt}</p>

                    {expanded && (
                      <div className="mt-3 pt-3 border-t border-dashed border-background-300 animate-fadeUp">
                        <p className="text-sm text-foreground-700 leading-relaxed font-body">{item.detail}</p>
                        <p className="mt-3 text-xs text-foreground-500 font-label flex items-center gap-1.5">
                          <i className="ri-information-line" aria-hidden="true"></i>
                          来源：{item.source}，仅供参考，不代表事实认定
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-[11px] font-label"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200">
                      <span className="text-xs text-foreground-500 font-label flex items-center gap-1">
                        <i className="ri-time-line" aria-hidden="true"></i>
                        {item.time}
                      </span>
                      <button
                        type="button"
                        onClick={() => setExpandedId(expanded ? null : item.id)}
                        aria-expanded={expanded}
                        className="inline-flex items-center gap-1 text-xs md:text-sm text-primary-600 hover:text-primary-700 font-label whitespace-nowrap cursor-pointer"
                      >
                        {expanded ? '收起详情' : '展开详情'}
                        <i
                          className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
                          aria-hidden="true"
                        ></i>
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}